import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbcCore } from '@barthauer/abc/core';
import {
  ObjectTypeInfo,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QuerySpan,
  SubQueryKind,
} from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { ColumnTypeIcon, CoreService } from '@barthauer/abc/queries/opath/core';
import { _AbcTreeComponent, _AbcTreeNode } from '@barthauer/abc/ui-components';
import { ItemClickEvent } from 'devextreme/ui/tree_view';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AbcQueriesEditorService } from '../../abc-queries-editor.service';
import { QueriesTreeService } from '../../services/queries-tree.service';
import {
  ColumnProperties,
  ColumnSpacing,
  Property,
  QueryProperties,
} from '../../viewModels/abc-queries-editor.viewModel';

@Component({
  selector: 'abc-opath-queries-editor-queries-tree',
  templateUrl: './queries-tree.component.html',
  styleUrls: ['./queries-tree.component.scss'],
})
export class QueriesTreeComponent implements OnInit, OnDestroy {
  @ViewChild('queriesTree', { static: true }) queriesTree: _AbcTreeComponent;
  @Output() nodeClickEvent = new EventEmitter<_AbcTreeNode<Property>>();

  public objectSpaceName: string;
  public queries: _AbcTreeNode<Property> = new _AbcTreeNode<Property>();
  public generalProperties: string;

  private selectedNode: _AbcTreeNode<Property>;
  private $unsubscribe: Subject<void> = new Subject();
  private resAsync: Promise<ResourceSet>;
  private deleteQueryConfirmation: string;

  constructor(
    private abcQueriesEditorService: AbcQueriesEditorService,
    private queriesTreeService: QueriesTreeService,
    private messageBoxService: MessageBoxService,
    private resourcesService: ResourceManagerService,
    private abcCore: AbcCore,
    private coreService: CoreService
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
  }

  async ngOnInit(): Promise<void> {
    try {
      const res = await this.resAsync;

      this.generalProperties = res.get('queryTree-title');
      this.deleteQueryConfirmation = res.get('queryTree-deleteQueryConfirmation');

      await this.createRootTreeNode();
      await this.loadQueries();

      this.queriesTreeService.expressionEditorInsertion.pipe(takeUntil(this.$unsubscribe)).subscribe((newNodes) => {
        if (newNodes) {
          this.insertNodes(newNodes);
        }
      });
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  public async loadQueries(): Promise<void> {
    if (
      this.abcQueriesEditorService.queryDocumentId ||
      this.abcQueriesEditorService.queryDocument?.ObjectQueries?.length
    ) {
      if (this.abcQueriesEditorService.queryDocumentId) {
        //loader stuff must be inside this if - otherwise the load-panel is visible forever
        this.coreService.loaderText = (await this.resAsync).get('queryEditor-loadingQuery');
        this.coreService.showLoader = true;
        this.abcQueriesEditorService.queryDocument = await this.queriesTreeService.readQueryFromDocument(
          this.abcQueriesEditorService.queryDocumentId
        );
      }
      await this.buildQueryFromDocument(this.queries, this.abcQueriesEditorService.queryDocument.ObjectQueries);
    }
    this.nodeClickEvent.emit(this.queries);
    this.queriesTree.instance.selectItem(this.queries);
    this.coreService.showLoader = false;
  }
  /**
   * refreshs the tree and emits its new data that takes in an
   * optional inBetween action to perform in between the refresh start and end.
   * @param inBetween
   * @returns void
   */
  public refreshTree = (inBetween?: void): void => {
    this.queriesTree.instance.beginUpdate();
    inBetween;
    if (this.selectedNode?.data?.queryProperties?.subQueryKind !== SubQueryKind.Sub) {
      for (let column of this.selectedNode.children) {
        if (column.data.columnProperties) {
          const uc = this.generateUniqueCaption(this.selectedNode, column.caption, this.selectedNode.caption);
          column.caption = uc;
          column.data.alias = uc;
        }
      }
    }
    const dataSource = this.queriesTree.instance.getDataSource();
    this.queriesTree.dataSource = dataSource;
    this.queriesTree.instance.endUpdate();
    this.queriesTree.itemsChange.emit(this.queriesTree.data);
  };

  public refreshRoot(): void {
    this.queries.caption = this.abcQueriesEditorService.rootElementName?.length
      ? this.abcQueriesEditorService.rootElementName
      : this.abcQueriesEditorService.objectSpaceName;
    this.objectSpaceName = this.abcQueriesEditorService.objectSpaceName;
    this.createRootTreeNode();
  }

  public createRootTreeNode = async (): Promise<void> => {
    this.queries.caption = this.abcQueriesEditorService.rootElementName?.length
      ? this.abcQueriesEditorService.rootElementName
      : this.abcQueriesEditorService.objectSpaceName;
    this.queries.id = this.objectSpaceName;
    this.queries.parentId = null;
    this.queries.icon = 'folder';
    this.queries.expanded = true;
    this.abcQueriesEditorService.queries = this.queries;
    if (!this.abcQueriesEditorService.queryDocumentId) {
      this.nodeClickEvent.emit(this.queries);
      this.queriesTree.instance.selectItem(this.queries);
    }
  };

  public drop(event: CdkDragDrop<any>): void {
    const objectType: ObjectTypeInfo = event.item.element.nativeElement['data-objectTypeInfo'];
    const newQuery = this.createTreeNode(objectType);
    this.generateNodeCaption(newQuery, this.queries);
    this.queriesTree.addNode(newQuery);
    this.queriesTree.instance.selectItem(newQuery);
    this.selectedNode = this.queriesTree.getNodeById(newQuery.id) as _AbcTreeNode<Property>;
    this.nodeClickEvent.emit(this.selectedNode);
  }

  private generateNodeCaption(node: _AbcTreeNode<Property>, parentNode: _AbcTreeNode<Property>) {
    const baseCaption = node.caption;

    const maxSuffix = parentNode.children
      .map((child: _AbcTreeNode<Property>) => child.caption.split('-'))
      .filter(
        (baseAndSuffixCaption: string[]) =>
          baseAndSuffixCaption[0] === baseCaption &&
          baseAndSuffixCaption[1] &&
          !isNaN(parseInt(baseAndSuffixCaption[1]))
      )
      .reduce((max: number, baseAndSuffixCaption: string[]) => Math.max(max, parseInt(baseAndSuffixCaption[1])), 0);

    if (maxSuffix > 0 || parentNode.children.some((child: _AbcTreeNode<Property>) => child.caption === baseCaption)) {
      node.caption = `${baseCaption}-${maxSuffix + 1}`;
      node.data.alias = node.caption;
    }
  }

  private async buildQueryFromDocument(
    parentNode: _AbcTreeNode<Property>,
    objectQueries: Query[] | QuerySpan[],
    isSubQuery?: boolean
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      objectQueries.map(async (query: Query) => {
        const newQuery = new _AbcTreeNode<Property>();
        newQuery.expanded = true;
        newQuery.id = this.queriesTreeService.generatePropertyId(
          parentNode.id + '_' + (query.ObjectType ?? query.OPath),
          parentNode
        );
        if (parentNode.id !== this.objectSpaceName) {
          newQuery.parentId = parentNode.id;
          newQuery.parentNode = parentNode;
        }
        newQuery.icon = ColumnTypeIcon.reference;
        newQuery.caption = query.Name ?? query.ObjectType ?? query.OPath;
        newQuery.data = new Property();
        if (isSubQuery) {
          newQuery.data.name = query.Name;
        } else {
          newQuery.data.name = query.ObjectType ?? query.OPath;
        }
        newQuery.data.expression = query.OPath;
        newQuery.data.alias = '';
        newQuery.data.queryProperties = new QueryProperties({
          skippedRowsNbr: 0,
          limitRowsNbr: 0,
          isDuplicated: false,
          subQueryKind:
            isSubQuery && !query.Name ? (query.DefaultIfEmpty ? SubQueryKind.LeftJoin : SubQueryKind.InnerJoin) : null,
        });

        query.Columns && this.buildColumnsFromDocument(newQuery, query.Columns);

        this.queriesTree.addNode(newQuery);
        this.queriesTree.dataSource = this.queriesTree.instance.getDataSource();

        query.ObjectQueries ? await this.buildQueryFromDocument(newQuery, query.ObjectQueries, true) : resolve();
      });
    });
  }

  private buildColumnsFromDocument(parentNode: _AbcTreeNode<Property>, queryColumns: QueryColumn[]): void {
    queryColumns.map(async (queryColumn: QueryColumn) => {
      const column = new _AbcTreeNode<Property>();
      const uc = this.generateUniqueCaption(parentNode, queryColumn.Name ?? queryColumn.OPath, parentNode.caption);
      column.caption = uc;
      column.icon = ColumnTypeIcon.box;
      column.id = this.queriesTreeService.generatePropertyId(parentNode.id + '_' + queryColumn.OPath, parentNode);
      column.parentId = parentNode.id;
      column.parentNode = parentNode;
      column.data = new Property();
      column.data.alias = uc;
      column.data.expression = queryColumn.OPath;
      column.data.name = queryColumn.OPath;
      column.data.columnProperties = new ColumnProperties({
        visible: true,
        sorting: QueryColumnSortOrder.None,
        spacing: ColumnSpacing.IGNOR_BLANK_PROPERTIES,
        formatExpression: '',
        culture: false,
        filter: false,
      });
      this.generateNodeCaption(column, column.parentNode as _AbcTreeNode<Property>);
      parentNode.children.push(column);
    });
  }

  /**
   * Generates a unique caption for a given column-caption and query-caption
   * by checking its parental path for occurences.
   * @param query
   * @param columnCaption
   * @returns
   */
  private generateUniqueCaption = (
    query: _AbcTreeNode<Property>,
    columnCaption: string,
    queryCaption: string
  ): string => {
    if (
      query.data?.queryProperties?.subQueryKind !== null &&
      query.data?.queryProperties?.subQueryKind !== SubQueryKind.Sub &&
      query.parentNode?.children.some((node) => node.caption === columnCaption)
    ) {
      return queryCaption + '-' + columnCaption;
    } else if (query.parentNode) {
      return this.generateUniqueCaption(query.parentNode as _AbcTreeNode<Property>, columnCaption, queryCaption);
    } else {
      return columnCaption;
    }
  };

  private createTreeNode(objectTypeInfo: ObjectTypeInfo): _AbcTreeNode<Property> {
    let suffix = 0;
    let newQueryId = objectTypeInfo.Name;

    while (this.queriesTree.items[0]['children'].some((node: _AbcTreeNode) => node.id === newQueryId)) {
      suffix++;
      newQueryId = `${objectTypeInfo.Name}-${suffix}`;
    }

    const newQuery = new _AbcTreeNode<Property>();
    newQuery.expanded = true;
    newQuery.id = newQueryId;
    newQuery.icon = ColumnTypeIcon.reference;
    newQuery.caption = objectTypeInfo.DisplayText;
    newQuery.data = new Property();
    newQuery.data.name = objectTypeInfo.Name;
    newQuery.data.alias = '';
    newQuery.data.queryProperties = new QueryProperties({
      skippedRowsNbr: 0,
      limitRowsNbr: 0,
      isDuplicated: false,
      subQueryKind: null,
    });

    return newQuery;
  }

  public navigateToProperty(event: ItemClickEvent): void {
    this.selectedNode = event.itemData as _AbcTreeNode<Property>;
    this.nodeClickEvent.emit(this.selectedNode);
  }

  public insertNodes(nodes: _AbcTreeNode<Property>[]) {
    nodes.forEach((node: _AbcTreeNode<Property>) => {
      this.generateNodeCaption(node, this.selectedNode);
      node.data.queryProperties ? (node.expanded = true) : null;
      if (node.data.columnProperties) {
        const uc = this.generateUniqueCaption(this.selectedNode, node.caption, this.selectedNode.caption);
        node.caption = uc;
        node.data.alias = uc;
      }

      setTimeout(() => {
        this.queriesTree.addNode(node);
      }, 0);
    });
  }
  private getNodeIndex(node: _AbcTreeNode<Property>): number {
    return node.parentNode ? node.parentNode.children.findIndex((selectedNode) => selectedNode === node) : -1;
  }

  public duplicateCurrentNode(): void {
    if (this.selectedNode.data.columnProperties) {
      this.queriesTree.addNode(this.createNewColumn());
    }
  }

  public deleteCurrentNode(): void {
    if (this.selectedNode.data.queryProperties) {
      const settings = new MessageSettings();
      settings.mode = MessageMode.Warning;
      settings.icon = MessageBoxIcon.Question;
      settings.button = MessageBoxButtons.YesNo;
      settings.message = this.deleteQueryConfirmation;
      this.messageBoxService
        .showMessageBox(settings)
        .pipe(takeUntil(this.$unsubscribe))
        .subscribe((result: DialogResult) => {
          if (DialogResult.Yes === result['result']) {
            const previousNodeIndex = this.selectedNodeIndex();
            if (this.selectedNode.parentId === this.objectSpaceName) {
              this.queriesTree.instance.beginUpdate();
              const dataSource = this.queriesTree.instance.getDataSource();
              this.queries.children.splice(previousNodeIndex, 1);
              this.queriesTree.dataSource = dataSource;
              this.queriesTree.instance.endUpdate();
            } else {
              this.queriesTree.removeNode(this.selectedNode);
              this.queriesTree.instance._refresh();
            }
            this.selectNewNode(previousNodeIndex);

            return;
          }
        });
    } else {
      const previousNodeIndex = this.selectedNodeIndex();
      this.queriesTree.removeNode(this.selectedNode);
      this.selectNewNode(previousNodeIndex);
      this.queriesTree.instance._refresh();
    }
  }

  private selectNewNode(previousNodeIndex: number): void {
    if (this.selectedNode.parentNode.children.length > 0) {
      if (previousNodeIndex >= 0 && previousNodeIndex < this.selectedNode.parentNode.children.length) {
        this.selectedNode = this.selectedNode.parentNode.children[previousNodeIndex] as _AbcTreeNode<Property>;
      } else {
        this.selectedNode = this.selectedNode.parentNode.children[0] as _AbcTreeNode<Property>;
      }
    } else {
      this.selectedNode = this.selectedNode.parentNode as _AbcTreeNode<Property>;
    }
    this.queriesTree.instance.selectItem(this.selectedNode);
    this.nodeClickEvent.emit(this.selectedNode);
  }

  private selectedNodeIndex(): number {
    return this.selectedNode.parentNode.children.findIndex(
      (node: _AbcTreeNode<Property>) => node.id === this.selectedNode.id
    );
  }

  private createNewColumn(): _AbcTreeNode<Property> {
    const newColumn: _AbcTreeNode<Property> = new _AbcTreeNode<Property>();
    let newColumnId = this.selectedNode.id;
    let newCaption = this.selectedNode.caption;
    let suffix = 0;
    while (this.selectedNode.parentNode.children.some((node: _AbcTreeNode) => node.id === newColumnId)) {
      suffix++;
      newColumnId = `${this.selectedNode.id}-${suffix}`;
      newCaption = `${this.selectedNode.caption}-${suffix}`;
    }
    newColumn.caption = newCaption;
    newColumn.id = newColumnId;
    const newColumnProperties = new ColumnProperties({
      visible: this.selectedNode.data.columnProperties.visible,
      sorting: this.selectedNode.data.columnProperties.sorting,
      culture: this.selectedNode.data.columnProperties.culture,
      filter: this.selectedNode.data.columnProperties.filter,
      formatExpression: this.selectedNode.data.columnProperties.formatExpression,
      spacing: this.selectedNode.data.columnProperties.spacing,
    });
    newColumn.data = new Property();
    newColumn.data.alias = newCaption;
    newColumn.data.columnProperties = newColumnProperties;
    newColumn.data.expression = this.selectedNode.data.expression;
    newColumn.data.name = this.selectedNode.data.name;

    newColumn.icon = this.selectedNode.icon;
    newColumn.parentId = this.selectedNode.parentId;
    newColumn.parentNode = this.selectedNode.parentNode;

    return newColumn;
  }

  public upwardNode(): void {
    const currentNodeIndex = this.getNodeIndex(this.selectedNode);
    if (currentNodeIndex > 0) {
      const parentNode = this.selectedNode.parentNode as _AbcTreeNode<Property>;
      this.refreshTree(this.moveNode(currentNodeIndex, currentNodeIndex - 1, parentNode));
    }
  }

  public downwardNode(): void {
    const currentNodeIndex = this.getNodeIndex(this.selectedNode);
    const parentNode = this.selectedNode.parentNode as _AbcTreeNode<Property>;
    if (parentNode && currentNodeIndex < parentNode.children.length - 1) {
      this.refreshTree(this.moveNode(currentNodeIndex, currentNodeIndex + 1, parentNode));
    }
  }

  private moveNode(currentIndex: number, targetIndex: number, parentNode: _AbcTreeNode<Property>): void {
    const nodeToMove = parentNode.children.splice(currentIndex, 1)[0];
    parentNode.children.splice(targetIndex, 0, nodeToMove);
    this.queriesTree.instance.selectItem(nodeToMove);
    this.nodeClickEvent.emit(nodeToMove);
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }
}
